import { useEffect, useState } from "react";
import api, { money, apiErr } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Wallet, Clock, CheckCircle2, IndianRupee, TrendingUp, Plus, History, Receipt, FileText, Trash2, Download, Building2 } from "lucide-react";
import { toast } from "sonner";

const MODES = ["Cash", "UPI", "Bank Transfer", "Cheque"];

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: true });
};

export default function AccountsPage() {
  const [data, setData] = useState(null);
  const [advFor, setAdvFor] = useState(null);
  const [amount, setAmount] = useState("");
  const [mode, setMode] = useState("Cash");
  const [histFor, setHistFor] = useState(null);
  const load = () => api.get("/accounts").then((r) => setData(r.data)).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { load(); }, []);

  const mark = async (id, st) => {
    try { await api.patch(`/leads/${id}/payment`, { payment_status: st }); toast.success(st === "paid" ? "Marked as Paid" : "Marked as Unpaid"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const submitAdvance = async () => {
    const amt = parseFloat(amount);
    if (isNaN(amt) || amt <= 0) return toast.error("Enter a valid amount");
    try {
      await api.post(`/leads/${advFor.id}/advance`, { amount: amt, mode });
      toast.success(`Advance of ${money(amt)} recorded via ${mode}`);
      setAdvFor(null); setAmount(""); setMode("Cash"); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const receipt = async (t) => {
    try {
      const res = await api.get(`/leads/${t.id}/documents/receipt`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `receipt-${t.order_number}.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Payment receipt generated");
    } catch (e) {
      let msg = apiErr(e);
      try { if (e?.response?.data instanceof Blob) msg = JSON.parse(await e.response.data.text()).detail || msg; } catch (_) {}
      toast.error(msg);
    }
  };

  const txns = data?.transactions || [];
  const pending = txns.filter((t) => t.payment_status !== "paid");
  const paid = txns.filter((t) => t.payment_status === "paid");
  const s = data?.summary || {};

  return (
    <div>
      <div className="mb-6">
        <div className="text-xs font-bold uppercase tracking-widest text-primary">Finance</div>
        <h1 className="font-heading font-black text-4xl tracking-tight">Account Management</h1>
        <p className="text-muted-foreground text-sm">Client ledger, advance payments, outstanding dues, and settled payments.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border border border-border mb-6">
        <Card label="Total Billed" value={money(s.total_billed)} icon={TrendingUp} tint="text-foreground" />
        <Card label="Received" value={money(s.total_received)} icon={CheckCircle2} tint="text-emerald-500" />
        <Card label="Outstanding" value={money(s.total_outstanding)} icon={IndianRupee} tint="text-primary" />
        <Card label="Pending Orders" value={s.pending_count ?? 0} icon={Clock} tint="text-amber-500" />
      </div>

      <Tabs defaultValue="ledger">
        <TabsList className="rounded-none bg-muted">
          <TabsTrigger value="ledger" className="rounded-none gap-2" data-testid="tab-ledger"><Wallet className="h-4 w-4" /> Ledger</TabsTrigger>
          <TabsTrigger value="pending" className="rounded-none gap-2" data-testid="tab-pending"><Clock className="h-4 w-4" /> Pending Orders</TabsTrigger>
          <TabsTrigger value="paid" className="rounded-none gap-2" data-testid="tab-paid"><CheckCircle2 className="h-4 w-4" /> Paid Orders</TabsTrigger>
          <TabsTrigger value="companies" className="rounded-none gap-2" data-testid="tab-companies"><Building2 className="h-4 w-4" /> By Company</TabsTrigger>
          <TabsTrigger value="vouchers" className="rounded-none gap-2" data-testid="tab-vouchers"><FileText className="h-4 w-4" /> Expense Vouchers</TabsTrigger>
        </TabsList>
        <TabsContent value="ledger"><Table rows={txns} mark={mark} onAdvance={setAdvFor} onHistory={setHistFor} onReceipt={receipt} testid="ledger-table" /></TabsContent>
        <TabsContent value="pending"><Table rows={pending} mark={mark} onAdvance={setAdvFor} onHistory={setHistFor} onReceipt={receipt} testid="pending-table" /></TabsContent>
        <TabsContent value="paid"><Table rows={paid} mark={mark} onAdvance={setAdvFor} onHistory={setHistFor} onReceipt={receipt} testid="paid-table" /></TabsContent>
        <TabsContent value="companies"><CompanyTable rows={data?.by_company || []} /></TabsContent>
        <TabsContent value="vouchers"><VouchersSection /></TabsContent>
      </Tabs>

      <Dialog open={!!advFor} onOpenChange={(o) => { if (!o) { setAdvFor(null); setAmount(""); setMode("Cash"); } }}>
        <DialogContent className="rounded-none" data-testid="advance-dialog">
          <DialogHeader><DialogTitle className="font-heading uppercase tracking-tight">Record Advance Payment</DialogTitle></DialogHeader>
          {advFor && (
            <div className="space-y-3">
              <div className="text-sm text-muted-foreground">Order <span className="font-mono-num text-primary">{advFor.order_number}</span> · {advFor.customer_name}</div>
              <div className="grid grid-cols-3 gap-2 text-sm border border-border p-3">
                <div><div className="text-[11px] uppercase text-muted-foreground">Total</div><div className="font-mono-num font-bold">{money(advFor.total)}</div></div>
                <div><div className="text-[11px] uppercase text-muted-foreground">Received</div><div className="font-mono-num font-bold text-emerald-500">{money(advFor.advance)}</div></div>
                <div><div className="text-[11px] uppercase text-muted-foreground">Outstanding</div><div className="font-mono-num font-bold text-primary">{money(advFor.outstanding)}</div></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label>Advance amount (₹)</Label>
                  <Input type="number" min="1" step="0.01" className="rounded-none font-mono-num h-11" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="e.g. 5000" data-testid="advance-amount" autoFocus />
                </div>
                <div className="space-y-2"><Label>Payment mode</Label>
                  <Select value={mode} onValueChange={setMode}>
                    <SelectTrigger className="rounded-none h-11" data-testid="advance-mode"><SelectValue /></SelectTrigger>
                    <SelectContent className="rounded-none">
                      {MODES.map((m) => <SelectItem key={m} value={m} data-testid={`mode-${m.replace(/\s/g, "-").toLowerCase()}`}>{m}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}
          <DialogFooter><Button className="rounded-none font-bold uppercase tracking-wide w-full" onClick={submitAdvance} data-testid="save-advance">Record Advance</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!histFor} onOpenChange={(o) => { if (!o) setHistFor(null); }}>
        <DialogContent className="rounded-none max-w-lg" data-testid="history-dialog">
          <DialogHeader><DialogTitle className="font-heading uppercase tracking-tight">Payment History</DialogTitle></DialogHeader>
          {histFor && (
            <div className="space-y-3">
              <div className="text-sm text-muted-foreground">Order <span className="font-mono-num text-primary">{histFor.order_number}</span> · {histFor.customer_name}</div>
              <div className="grid grid-cols-3 gap-2 text-sm border border-border p-3">
                <div><div className="text-[11px] uppercase text-muted-foreground">Total</div><div className="font-mono-num font-bold">{money(histFor.total)}</div></div>
                <div><div className="text-[11px] uppercase text-muted-foreground">Received</div><div className="font-mono-num font-bold text-emerald-500">{money(histFor.advance)}</div></div>
                <div><div className="text-[11px] uppercase text-muted-foreground">Outstanding</div><div className="font-mono-num font-bold text-primary">{money(histFor.outstanding)}</div></div>
              </div>
              <div className="border border-border max-h-80 overflow-y-auto" data-testid="history-list">
                {(!histFor.payments || histFor.payments.length === 0) ? (
                  <div className="p-8 text-center text-sm text-muted-foreground">No payments recorded yet.</div>
                ) : (
                  <table className="w-full text-sm">
                    <thead><tr className="border-b border-border text-left text-[11px] uppercase tracking-wide text-muted-foreground bg-muted">
                      <th className="py-2 px-3">Date &amp; Time</th><th className="px-3">Mode</th><th className="px-3 text-right">Amount</th><th className="px-3 text-right">Balance</th><th className="px-3">By</th>
                    </tr></thead>
                    <tbody>
                      {histFor.payments.map((p, i) => (
                        <tr key={p.id || i} className="border-b border-border last:border-0" data-testid={`history-row-${i}`}>
                          <td className="px-3 py-2 text-xs whitespace-nowrap">{fmtDateTime(p.at)}</td>
                          <td className="px-3"><span className="inline-flex items-center px-2 py-0.5 text-[10px] font-bold uppercase border border-border bg-muted">{p.mode || "—"}</span></td>
                          <td className="px-3 text-right font-mono-num text-emerald-600 dark:text-emerald-400">{money(p.amount)}</td>
                          <td className="px-3 text-right font-mono-num">{money(p.balance_after)}</td>
                          <td className="px-3 text-xs text-muted-foreground">{p.by || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function VouchersSection() {
  const [vouchers, setVouchers] = useState([]);
  const [lrs, setLrs] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", towards: "", amount: "", lrKey: "", remarks: "", date: new Date().toISOString().slice(0, 10) });
  const load = () => api.get("/vouchers").then((r) => setVouchers(r.data)).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { load(); api.get("/lrs/available").then((r) => setLrs(r.data)).catch(() => {}); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    const amt = parseFloat(form.amount);
    if (isNaN(amt) || amt <= 0) return toast.error("Enter a valid amount");
    const lr = lrs.find((x) => `${x.lead_id}:${x.lr_id}` === form.lrKey);
    try {
      await api.post("/vouchers", {
        name: form.name, towards: form.towards, amount: amt, remarks: form.remarks, date: form.date,
        lead_id: lr?.lead_id || "", lr_id: lr?.lr_id || "", lr_number: lr?.lr_number || "", order_number: lr?.order_number || "",
      });
      toast.success("Voucher created");
      setOpen(false); setForm({ name: "", towards: "", amount: "", lrKey: "", remarks: "", date: new Date().toISOString().slice(0, 10) });
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const download = async (v) => {
    try {
      const res = await api.get(`/vouchers/${v.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `${v.serial.replace(/\//g, "-")}-voucher.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiErr(e)); }
  };
  const del = async (v) => { if (!window.confirm(`Delete voucher ${v.serial}?`)) return; try { await api.delete(`/vouchers/${v.id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(apiErr(e)); } };

  return (
    <div className="mt-4">
      <div className="flex justify-between items-center mb-3">
        <p className="text-sm text-muted-foreground">Custom expense vouchers tagged to a specific LR — with company letterhead PDF.</p>
        <Button className="rounded-none gap-2 font-bold uppercase tracking-wide" onClick={() => setOpen(true)} data-testid="voucher-add"><Plus className="h-4 w-4" /> New Voucher</Button>
      </div>
      <div className="border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm" data-testid="vouchers-table">
          <thead><tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
            <th className="py-3 px-4">Voucher</th><th className="px-4">Name</th><th className="px-4">Towards</th><th className="px-4">LR No.</th><th className="px-4 text-right">Amount</th><th className="px-4">Date</th><th className="px-4 text-right">Action</th>
          </tr></thead>
          <tbody>
            {vouchers.length === 0 && <tr><td colSpan={7} className="p-10 text-center text-muted-foreground">No vouchers yet.</td></tr>}
            {vouchers.map((v) => (
              <tr key={v.id} className="border-b border-border last:border-0 hover:bg-muted transition-colors" data-testid={`voucher-row-${v.id}`}>
                <td className="px-4 py-3 font-mono-num text-primary text-xs">{v.serial}</td>
                <td className="px-4">{v.name}</td>
                <td className="px-4 text-muted-foreground">{v.towards || "—"}</td>
                <td className="px-4 font-mono-num text-xs">{v.lr_number || "—"}</td>
                <td className="px-4 text-right font-mono-num font-bold">{money(v.amount)}</td>
                <td className="px-4 text-xs text-muted-foreground">{v.date}</td>
                <td className="px-4 text-right whitespace-nowrap">
                  <Button size="sm" variant="outline" className="rounded-none text-xs gap-1 mr-1" onClick={() => download(v)} data-testid={`voucher-dl-${v.id}`}><Download className="h-3 w-3" /> PDF</Button>
                  <button className="h-8 w-8 inline-grid place-items-center text-muted-foreground hover:text-destructive align-middle" onClick={() => del(v)} data-testid={`voucher-del-${v.id}`}><Trash2 className="h-4 w-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none" data-testid="voucher-dialog">
          <DialogHeader><DialogTitle className="font-heading uppercase tracking-tight">Custom Expense Voucher</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label>Name *</Label><Input className="rounded-none mt-1" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Paid to / person name" data-testid="voucher-f-name" /></div>
            <div><Label>Towards (Category)</Label><Input className="rounded-none mt-1" value={form.towards} onChange={(e) => set("towards", e.target.value)} placeholder="e.g. Loading Labour, Toll, Diesel" data-testid="voucher-f-towards" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Amount (₹) *</Label><Input type="number" min="1" step="0.01" className="rounded-none mt-1 font-mono-num" value={form.amount} onChange={(e) => set("amount", e.target.value)} data-testid="voucher-f-amount" /></div>
              <div><Label>Date</Label><Input type="date" className="rounded-none mt-1" value={form.date} onChange={(e) => set("date", e.target.value)} /></div>
            </div>
            <div><Label>Link to LR</Label>
              <select className="rounded-none mt-1 h-10 w-full bg-background border border-border px-2 text-sm" value={form.lrKey} onChange={(e) => set("lrKey", e.target.value)} data-testid="voucher-f-lr">
                <option value="">— Select LR —</option>
                {lrs.map((l) => <option key={`${l.lead_id}:${l.lr_id}`} value={`${l.lead_id}:${l.lr_id}`}>{l.lr_number || l.order_number} · {l.customer_name} · {l.route}</option>)}
              </select>
            </div>
            <div><Label>Remarks</Label><Input className="rounded-none mt-1" value={form.remarks} onChange={(e) => set("remarks", e.target.value)} /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" className="rounded-none" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="rounded-none font-bold uppercase tracking-wide" onClick={save} data-testid="voucher-save">Create Voucher</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Card({ label, value, icon: Icon, tint }) {
  return (
    <div className="bg-card p-5" data-testid={`acct-stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-center justify-between"><span className="text-xs uppercase tracking-wide text-muted-foreground">{label}</span><Icon className={`h-4 w-4 ${tint}`} /></div>
      <div className="font-mono-num font-bold text-2xl mt-3">{value}</div>
    </div>
  );
}

function CompanyTable({ rows }) {
  const tot = rows.reduce((a, r) => ({ billed: a.billed + r.billed, received: a.received + r.received, outstanding: a.outstanding + r.outstanding, orders: a.orders + r.orders }), { billed: 0, received: 0, outstanding: 0, orders: 0 });
  return (
    <div className="border border-border bg-card overflow-x-auto mt-4">
      <table className="w-full text-sm" data-testid="company-table">
        <thead><tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
          <th className="py-3 px-4">Bill To Company</th><th className="px-4 text-right">Orders</th>
          <th className="px-4 text-right">Billed</th><th className="px-4 text-right">Received</th><th className="px-4 text-right">Outstanding</th>
        </tr></thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={5} className="p-10 text-center text-muted-foreground">No records.</td></tr>}
          {rows.map((r) => (
            <tr key={r.company} className="border-b border-border last:border-0 hover:bg-muted transition-colors" data-testid={`company-row-${r.company}`}>
              <td className="px-4 py-3 font-medium">{r.company}</td>
              <td className="px-4 text-right font-mono-num text-muted-foreground">{r.orders}</td>
              <td className="px-4 text-right font-mono-num">{money(r.billed)}</td>
              <td className="px-4 text-right font-mono-num text-emerald-600 dark:text-emerald-400">{money(r.received)}</td>
              <td className="px-4 text-right font-mono-num font-bold text-primary">{money(r.outstanding)}</td>
            </tr>
          ))}
        </tbody>
        {rows.length > 0 && (
          <tfoot><tr className="border-t-2 border-border bg-muted font-bold" data-testid="company-total-row">
            <td className="px-4 py-3 uppercase text-xs tracking-wide">Total</td>
            <td className="px-4 text-right font-mono-num">{tot.orders}</td>
            <td className="px-4 text-right font-mono-num">{money(tot.billed)}</td>
            <td className="px-4 text-right font-mono-num text-emerald-600 dark:text-emerald-400">{money(tot.received)}</td>
            <td className="px-4 text-right font-mono-num text-primary">{money(tot.outstanding)}</td>
          </tr></tfoot>
        )}
      </table>
    </div>
  );
}

function Table({ rows, mark, onAdvance, onHistory, onReceipt, testid }) {
  const badgeCls = (st) => st === "paid"
    ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400 border-emerald-300 dark:border-emerald-800"
    : st === "partial"
      ? "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400 border-blue-300 dark:border-blue-800"
      : "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400 border-amber-300 dark:border-amber-800";
  return (
    <div className="border border-border bg-card overflow-x-auto mt-4">
      <table className="w-full text-sm" data-testid={testid}>
        <thead><tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
          <th className="py-3 px-4">Date</th><th className="px-4">Order</th><th className="px-4">LR Number</th><th className="px-4">Invoice No.</th><th className="px-4">Client</th>
          <th className="px-4 text-right">Total</th><th className="px-4 text-right">Received</th><th className="px-4 text-right">Outstanding</th><th className="px-4">Payment</th><th className="px-4 text-right">Action</th>
        </tr></thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={10} className="p-10 text-center text-muted-foreground">No records.</td></tr>}
          {rows.map((t) => (
            <tr key={t.id} className="border-b border-border last:border-0 hover:bg-muted transition-colors" data-testid={`acct-row-${t.order_number}`}>
              <td className="px-4 py-3 text-xs text-muted-foreground">{new Date(t.created_at).toLocaleDateString()}</td>
              <td className="px-4 font-mono-num text-primary">{t.order_number}</td>
              <td className="px-4 font-mono-num text-xs">{t.lr_number || "—"}</td>
              <td className="px-4 font-mono-num text-xs" data-testid={`acct-invoice-${t.order_number}`}>{(t.invoice_numbers || []).length ? t.invoice_numbers.join(", ") : "—"}</td>
              <td className="px-4">{t.customer_name}</td>
              <td className="px-4 text-right font-mono-num">{money(t.total)}</td>
              <td className="px-4 text-right font-mono-num text-emerald-600 dark:text-emerald-400">{money(t.advance)}</td>
              <td className="px-4 text-right font-mono-num font-bold">{money(t.outstanding)}</td>
              <td className="px-4">
                <span className={`inline-flex items-center px-2 py-0.5 text-[11px] font-bold uppercase border ${badgeCls(t.payment_status)}`}>{t.payment_status}</span>
              </td>
              <td className="px-4 text-right whitespace-nowrap">
                <Button size="sm" variant="outline" className="rounded-none text-xs gap-1 mr-1" onClick={() => onReceipt(t)} data-testid={`gen-receipt-${t.order_number}`}><Receipt className="h-3 w-3" /> Receipt</Button>
                <Button size="sm" variant="ghost" className="rounded-none text-xs gap-1 mr-1" onClick={() => onHistory(t)} data-testid={`view-history-${t.order_number}`}><History className="h-3 w-3" /> History</Button>
                {t.payment_status !== "paid" && (
                  <Button size="sm" variant="outline" className="rounded-none text-xs gap-1 mr-1" onClick={() => onAdvance(t)} data-testid={`add-advance-${t.order_number}`}><Plus className="h-3 w-3" /> Advance</Button>
                )}
                {t.payment_status === "paid"
                  ? <span className="text-[11px] text-emerald-600 dark:text-emerald-400 font-bold uppercase">Settled</span>
                  : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
